import classnames from 'classnames';
import * as React from 'react';
import { observer } from 'mobx-react';

import { EstimateDescriptionContainer } from '../../estimate-description/estimate-description';
import { EstimateSummaryContainer } from '../../estimate-summary/estimate-summary';
import { EstimateItemImages } from 'src/estimate/components/estimate-item-images/estimate-item-images';
import { PriceOptions, PresentationSettingsState } from '../../../stores/estimate.store';
import { hasMemberSavings, memberSavings } from '../../../util/member-savings';
import { Estimate } from 'src/api';
import { currency } from 'src/util/formatters';

import styles from './estimate-details.less';

interface Props {
    estimate: Estimate;
    priceOptions: PriceOptions;
    shouldShowApplyForFinancing: boolean;
    financingPresentationSetting?: PresentationSettingsState | undefined;
    gotoMobile: () => void;
}

export const EstimateDetails = observer<React.SFC<Props>>(
    ({ estimate, priceOptions, shouldShowApplyForFinancing, financingPresentationSetting, gotoMobile }) => (
        <div>
            <EstimateSummaryContainer
                estimate={estimate}
                shouldShowApplyForFinancing={shouldShowApplyForFinancing}
                financingPresentationSetting={financingPresentationSetting}
                gotoMobile={gotoMobile}
            />
            <div className={styles.items}>
                {estimate.items.map((item, index) => (
                    <div key={index} className={classnames('hr', styles.itemWrapper)}>
                        <div className={styles.item}>
                            <div className={styles.img}>
                                <EstimateItemImages imageUris={item.imageUris} />
                            </div>
                            <div className={styles.details}>
                                <div className={styles.name}>{item.name}</div>
                                {priceOptions === PriceOptions.Show && (
                                    <div className={styles.prices}>
                                        {hasMemberSavings(item) && estimate.isMember && (
                                            <div>
                                                <div>Standard Price</div>
                                                <div className={styles.standardPrice}>
                                                    {currency(item.standardPrice || 0)}
                                                </div>
                                            </div>
                                        )}
                                        {hasMemberSavings(item) && (
                                            <div>
                                                <div>
                                                    {estimate.isMember
                                                        ? 'Member Savings'
                                                        : 'Member Price'}
                                                </div>
                                                <div className={styles.memberSavings}>
                                                    {memberSavings(item, estimate.isMember)}
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <div>Your Price</div>
                                            <div>{currency(item.yourPrice || 0)}</div>
                                        </div>
                                    </div>
                                )}
                                <EstimateDescriptionContainer item={item} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {priceOptions !== PriceOptions.Hide && (
                <React.Fragment>
                    <div className={styles.totalsSeparator} />
                    <div
                        className={classnames(styles.totals, {
                            [styles.accepted]: estimate.isAccepted,
                        })}
                    >
                        <div>
                            <div>Subtotal</div>
                            <div>{currency(estimate.subtotal || 0)}</div>
                        </div>
                        {typeof estimate.tax === 'number' && (
                            <div>
                                <div>Tax</div>
                                <div>{currency(estimate.tax)}</div>
                            </div>
                        )}
                        {estimate.isMember && hasMemberSavings(estimate) && (
                            <div className={styles.memberSavings}>
                                <div>Member Savings</div>
                                <div>{memberSavings(estimate)}</div>
                            </div>
                        )}
                        <hr />
                        <div className={styles.total}>
                            <div>Total</div>
                            <div>{currency(estimate.total || 0)}</div>
                        </div>
                        {!estimate.isMember && hasMemberSavings(estimate) && (
                            <div
                                className={classnames(
                                    'gray-text centered-content',
                                    styles.potentialSavings
                                )}
                            >
                                Did you know that with a membership, you could have saved{' '}
                                {memberSavings(estimate, true, true)}?
                            </div>
                        )}
                    </div>
                </React.Fragment>
            )}
        </div>
    )
);
